import { z } from 'zod';
/**
 * The language-review sheet: the phrase dictionary handed to chess reviewers
 * for triage, one row per "wrong -> right" pair.
 *
 * The reviewer's job is deliberately tiny per row: confirm or override a
 * proposed priority. `proposed` is the pre-selected answer, so a reviewer who
 * agrees with a row does not have to touch it at all; only disagreements cost
 * a click. The export therefore distinguishes an explicit answer from a
 * proposal left standing.
 */
export const LANGUAGE_SCHEMA_VERSION = 1;
/** W = never, S = fix it, N = taste/regional, OK = the phrase is fine. */
export const languageChoice = z.enum(['W', 'S', 'N', 'OK']);
/** What a fresh row shows before the reviewer touches it. */
export const languageProposed = z.enum(['W', 'S', 'N']);
export const languageItemSchema = z.object({
    /** Stable id ("A9"), also the anchor the export files answers under. */
    id: z.string().min(1),
    /** The phrasing under judgement, verbatim — presented without a verdict. */
    bad: z.string().optional(),
    /** Definition sheets: the dictionary term and its wording. */
    term: z.string().optional(),
    definition: z.string().optional(),
    /** Our proposed replacement, shown as a proposal and nothing more. */
    good: z.string().optional(),
    /**
     * True when `good` is itself a concrete phrase a comment could use — then
     * the reviewer rates it on the same scale, independently of `bad`; both can
     * come out fine. False when `good` is guidance ("nazwij pole..."), which is
     * shown but not rated.
     */
    rateGood: z.boolean().default(false),
    /** A real sentence from an earlier report that uses the phrasing, if any. */
    example: z.string().optional(),
    proposed: languageProposed,
});
export const languageSectionSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    description: z.string().default(''),
    items: z.array(languageItemSchema).min(1),
});
export const languageSheetSchema = z.object({
    schemaVersion: z.literal(LANGUAGE_SCHEMA_VERSION),
    slug: z.string().regex(/^[a-z0-9-]+$/),
    /**
     * What the rows are and what the buttons mean. `phrases` is the wrong/right
     * triage; `definitions` rates dictionary terms by how common they are.
     */
    kind: z.enum(['phrases', 'definitions']).default('phrases'),
    title: z.string().min(1),
    intro: z.string(),
    sections: z.array(languageSectionSchema).min(1),
});
/**
 * One saved row: the verdict on the phrase, the independent verdict on the
 * proposed replacement (when it is rateable), and the reviewer's own words.
 */
export const languageAnswerSchema = z.object({
    choice: languageChoice.nullable().optional(),
    goodChoice: languageChoice.nullable().optional(),
    note: z.string().max(2000).nullable().optional(),
});
export const languageSubmitSchema = z.object({
    general: z.string().max(8000).nullable().optional(),
});
export const languageInviteInputSchema = z.object({
    sheetSlug: z.string(),
    reviewerId: z.string(),
});
/**
 * The four buttons answer one question, printed above the list:
 * "Czy tak można napisać w komentarzu pod diagramem?"
 */
export const LANGUAGE_CHOICE_OPTIONS = [
    { value: 'W', label: 'Nigdy', tone: 'red' },
    { value: 'S', label: 'Unikać', tone: 'amber' },
    { value: 'N', label: 'Może być', tone: 'neutral' },
    { value: 'OK', label: 'Tak się mówi', tone: 'green' },
];
/**
 * The definition sheet's three degrees: how common the term is at the board.
 * Stored in the same `choice` column; the sheet's `kind` says how to read it.
 */
export const DEFINITION_CHOICE_OPTIONS = [
    { value: 'W', label: 'Częste', tone: 'green' },
    { value: 'S', label: 'Spotykane', tone: 'amber' },
    { value: 'N', label: 'Nieużywane', tone: 'red' },
];
//# sourceMappingURL=language.js.map